import React from "react";
import { Button } from "antd";
import classNames from "classnames";
import "./CustomButton.scss";

const CustomButton = ({
  type = "filled",
  svgIcon,
  iconPosition, //En caso de tener valor "center", se asume que se mostrara sólo el icono y no se mostrara texto.
  children,
  onClick,
  disabled,
  id,
  className,
}) => {
  const buttonClasses = classNames("custom-button", {
    filled: type === "filled",
    outlined: type === "outlined",
    text: type === "text",
    "has-prefix": iconPosition === "left" && svgIcon,
    "has-suffix": iconPosition === "right" && svgIcon,
    "only-icon": iconPosition === "center" && svgIcon,
    "only-text": !svgIcon,
  });

  const svgFill = `var(${disabled ? "--neutral-40" : "--primary-00"})`;

  return (
    <Button
      id={id}
      className={`${buttonClasses} ${className}`}
      onClick={onClick}
      disabled={disabled}
    >
      {iconPosition === "left" && svgIcon && (
        <svg
          className="prefix-icon"
          width={svgIcon.width}
          height={svgIcon.height}
          viewBox={svgIcon.viewBox}
          xmlns="http://www.w3.org/2000/svg"
          fill={svgFill}
        >
          {svgIcon.path}
        </svg>
      )}
      {iconPosition === "center" && svgIcon && (
        <svg
          className="icon-only"
          width={svgIcon.width}
          height={svgIcon.height}
          viewBox={svgIcon.viewBox}
          xmlns="http://www.w3.org/2000/svg"
          fill={svgFill}
        >
          {svgIcon.path}
        </svg>
      )}
      {iconPosition !== "center" && children}
      {iconPosition === "right" && svgIcon && (
        <svg
          className="suffix-icon"
          width={svgIcon.width}
          height={svgIcon.height}
          viewBox={svgIcon.viewBox}
          xmlns="http://www.w3.org/2000/svg"
          fill={svgFill}
        >
          {svgIcon.path}
        </svg>
      )}
    </Button>
  );
};

export default CustomButton;

/************** IMPORTANTE **************
 USO DEL COMPONENTE:

 Descripción: `CustomButton` es un botón personalizado que permite diferentes estilos y la opción de incluir íconos SVG en distintas posiciones. Es versátil para adaptarse a múltiples situaciones de interfaz de usuario.

 Props:
 - `type` (String): Define el estilo del botón. Puede ser "filled", "outlined" o "text". Por defecto es "filled".
 - `svgIcon` (Object): Un objeto que contiene las propiedades `width`, `height`, `viewBox` y `path` para definir un ícono SVG que se puede mostrar en el botón.
 - `iconPosition` (String): Establece la posición del ícono en relación con el texto. Puede ser "left", "right", o "center". Si es "center", solo se mostrará el ícono sin texto.
 - `children` (Node): El contenido textual del botón.
 - `onClick` (Function): Función que se ejecuta cuando se hace clic en el botón.
 - `disabled` (Boolean): Desactiva el botón si es true, deshabilitando la interacción.
 - `id` (String): Identificador único para el botón.
 - `className` (String): Clases adicionales para personalizar el estilo del botón.

 Ejemplo de Uso:
 A continuación se muestra cómo usar `CustomButton` con diferentes estilos, íconos, y configuraciones.

       <CustomButton
        type="filled"
        svgIcon={{
          width: 16,
          height: 16,
          viewBox: "0 0 16 16",
          path: <path d="M1 1h14v14H1z" />
        }}
        iconPosition="left"
        onClick={() => alert('Botón clicado')}
      >
        Click Me
      </CustomButton>

       <CustomButton
        type="outlined"
        iconPosition="center"
        svgIcon={{
          width: 20,
          height: 20,
          viewBox: "0 0 20 20",
          path: <path d="M2 2h16v16H2z" />
        }}
      />

       <CustomButton
        type="text"
        iconPosition="right"
        svgIcon={{
          width: 12,
          height: 12,
          viewBox: "0 0 12 12",
          path: <path d="M3 3h6v6H3z" />
        }}
      >
        Learn More
      </CustomButton>

 Variantes:
 - `type` puede ser "filled" para un botón sólido, "outlined" para un botón con borde, o "text" para un botón solo de texto.
 - `iconPosition` determina si el ícono aparece a la izquierda, derecha o se centra, ocultando el texto.

       <CustomButton
        type="filled"
        iconPosition="center"
        svgIcon={{
          width: 24,
          height: 24,
          viewBox: "0 0 24 24",
          path: <path d="M4 4h16v16H4z" />
        }}
      />

       <CustomButton
        type="outlined"
        iconPosition="right"
      >
        Next
      </CustomButton>

 Notas:
 - Asegúrate de que `svgIcon` sea un objeto con propiedades `width`, `height`, `viewBox`, y `path` para que el ícono se renderice correctamente. Ver ejemplos en images/icons/svg
 - Si `iconPosition` es "center", el texto del botón (`children`) no se mostrará.
 - El color del ícono se ajusta automáticamente según si el botón está deshabilitado o no.

*****************************************/
